import { useState, useEffect, type FormEvent } from "react";
import { useToast, useColorModeValue } from "@chakra-ui/react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, VStack, Text,
} from "@chakra-ui/react";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminPermission } from "../../../types/admin";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  permission: AdminPermission | null;
  onSuccess: () => void;
}

export function PermissionFormModal({ isOpen, onClose, permission, onSuccess }: Props) {
  const toast = useToast();

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const labelColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");

  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (permission) {
      setCode(permission.code);
      setDescription(permission.description || "");
    } else {
      setCode("");
      setDescription("");
    }
  }, [permission, isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!code) return toast({ title: "O código é obrigatório", status: "warning", duration: 3000, isClosable: true });

    try {
      setSubmitting(true);
      if (permission) {
        await adminApi.updatePermission(permission.id, { code, description });
      } else {
        await adminApi.createPermission({ code, description });
      }
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast({ title: detail || "Erro ao salvar permissão", status: "error", duration: 5000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} borderRadius="2xl" mx={4}>
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          {permission ? "Editar Permissão" : "Nova Permissão"}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt={5}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>
                Código da Permissão
              </FormLabel>
              <Input
                placeholder="Ex: admin.users.manage"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                fontFamily="mono"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
              <Text fontSize="xs" color={descColor} mt={1}>
                Use pontos para criar hierarquia, ex: módulo.recurso.ação
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>
                Descrição
              </FormLabel>
              <Input
                placeholder="Explique o que essa permissão libera..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
          <Button variant="ghost" borderRadius="50px" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            type="submit"
            isLoading={submitting}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
